<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import {
  DgrDialog,
  DgrIcon,
  DgrPopover,
  DgrToggleButton,
} from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import QuestionTooltip from '@/components/common/atoms/question-tooltip.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Header from '@/components/layouts/header.vue';
import { STATES, useAdminSamlFederation } from '@/utils/swr';

const router = useRouter();
const { createSnackbar } = useSnackbar();

const { data: samlFederation, state, mutate } = useAdminSamlFederation();
const isActivated = ref(false);

const isLoaded = computed(() => state.value === STATES.SUCCESS);

onMounted(() => {
  // SSOのテスト実行して作成してから一覧にリダイレクトされる時、すでにsuccessになっているので再度取得する
  if (state.value === STATES.SUCCESS) {
    mutate();
  }
});

watch(samlFederation, () => {
  isActivated.value = samlFederation.value?.activated ?? false;
});

const hasMetadataUrl = computed(() => {
  return !!samlFederation.value?.federation_metadata_url;
});

const createSamlFederation = () => {
  if (hasMetadataUrl.value) {
    return;
  }
  router.push({
    name: 'samlFederationDetail',
  });
};

const isActivateDialogOpen = ref(false);
const activateDialogTitle = computed(() => {
  return samlFederation.value?.activated
    ? 'SSO認証を無効にしますか？'
    : 'SSO認証を有効にしますか？';
});
const activateDialogDescription = computed(() => {
  return samlFederation.value?.activated
    ? 'SSO認証を無効にすると、すべてのメンバーはSSO認証を使用してログインできなくなります。代わりに、通常のログイン方法（メールアドレスとパスワード）を利用する必要があります。'
    : 'SSO認証を有効にすると、すべてのメンバーがSSO認証以外のログイン方法でログインできなくなります。';
});
const activateDialogButtonLabel = computed(() => {
  return samlFederation.value?.activated ? '無効化' : '有効化';
});

const cancelActivateSamlFederation = () => {
  isActivated.value = samlFederation.value?.activated ?? false;
  isActivateDialogOpen.value = false;
};

watch(isActivateDialogOpen, isOpen => {
  if (!isOpen) {
    isActivated.value = samlFederation.value?.activated ?? false;
  }
});

const submitActivateSamlFederation = async () => {
  const newActivated = !samlFederation.value?.activated;
  try {
    await api.updateSamlFederation({
      activated: newActivated,
    });
    await mutate();
    createSnackbar({
      message: newActivated
        ? 'SSO認証を有効にしました'
        : 'SSO認証を無効にしました',
      type: 'success',
    });
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 400) {
      // 有効している間に別のユーザーが設定を削除した時400になる
      await mutate();
      cancelActivateSamlFederation();
      isActivateErrorDialogOpen.value = true;
    }
  }
  isActivateDialogOpen.value = false;
};

const isDeleteDialogOpen = ref(false);

const deleteMessage = computed(() =>
  samlFederation.value?.activated
    ? '登録中のIdP設定を削除するとSSO認証が自動で無効化されます。すべてのメンバーはSSO認証を使用してログインできなくなります。代わりに、通常のログイン方法を利用する必要があります。よろしいですか？'
    : '登録中のIdP設定を削除するとSSO認証を使用できなくなります。引き続き、通常のログイン方法でご利用することができます。よろしいですか？',
);

const deleteSamlFederation = async () => {
  await api.updateSamlFederation({
    name: '',
    metadataUrl: '',
    activated: false,
  });
  createSnackbar({
    message: '登録中のIdP設定を削除しました',
    type: 'success',
  });
  mutate();
  isDeleteDialogOpen.value = false;
};

const isActivateErrorDialogOpen = ref(false);
</script>

<template>
  <div class="saml-federation-setting">
    <Header title="SSO認証" header-width="100%" />
    <div class="saml-federation-content">
      <div class="body" v-if="isLoaded">
        <div class="title">
          <span class="c-title-bold--m">SAML2.0認証</span>
          <button
            class="c-btn c-btn--small c-btn--primary"
            :class="{
              disabled: hasMetadataUrl,
            }"
            @click="createSamlFederation"
          >
            IdP設定の登録
          </button>
        </div>
        <div class="activate">
          <DgrToggleButton
            v-model="isActivated"
            :disabled="!hasMetadataUrl"
            @click="hasMetadataUrl ? (isActivateDialogOpen = true) : undefined"
          >
            <span class="c-text-regular--s">SSO認証を有効にする</span>
          </DgrToggleButton>
          <QuestionTooltip class="activate-tooltip">
            <span>
              SSO認証を有効にすると、登録したアイデンティティプロバイダー（IdP）を経由してログインできるようになります。
            </span>
          </QuestionTooltip>
        </div>
        <div class="sso-card">
          <div class="table-title">
            <span class="c-text-regular--s">設定名</span>
          </div>
          <div class="divider"></div>
          <div v-if="hasMetadataUrl" class="federation-info">
            <span class="c-text-regular--m">{{
              samlFederation?.name ?? ''
            }}</span>
            <DgrPopover>
              <template #default="{ triggerProps }">
                <button v-bind="triggerProps" type="button" class="menu-button">
                  <DgrIcon size="small" name="ellipsis-h" /></button
              ></template>
              <template #content="{ close }">
                <div class="item-container">
                  <button
                    class="item c-text-regular--m"
                    @click="
                      router.push({
                        name: 'samlFederationDetail',
                      });
                      close();
                    "
                  >
                    <DgrIcon size="small" name="pencil" />編集
                  </button>
                  <div class="separator"></div>
                  <button
                    class="item c-text-regular--m"
                    @click="
                      isDeleteDialogOpen = true;
                      close();
                    "
                  >
                    <DgrIcon size="small" name="trash" />削除
                  </button>
                </div>
              </template>
            </DgrPopover>
          </div>
          <span v-else class="c-text-regular--m"
            >IdPは登録されていません。IdP設定の登録ボタンから登録してください。</span
          >
        </div>
      </div>
      <DgrDialog
        v-model:open="isDeleteDialogOpen"
        title="登録中のIdP設定を削除しますか？"
      >
        <template #description>
          <span class="saml-dialog">
            {{ deleteMessage }}
          </span>
        </template>
        <template #submit>
          <button class="delete-button" @click="deleteSamlFederation">
            削除
          </button>
        </template>
      </DgrDialog>
      <DgrDialog
        v-model:open="isActivateDialogOpen"
        :title="activateDialogTitle"
        :submit-label="activateDialogButtonLabel"
        @cancel="cancelActivateSamlFederation"
        @submit="submitActivateSamlFederation"
      >
        <template #description>
          <span class="saml-dialog">
            {{ activateDialogDescription }}
          </span>
        </template>
      </DgrDialog>
      <DgrDialog
        v-model:open="isActivateErrorDialogOpen"
        title="SSO認証を有効にできません"
        submit-label="閉じる"
        @submit="isActivateErrorDialogOpen = false"
      >
        <template #description>
          <span class="saml-dialog">
            登録中のIdPが削除されたため、SSO認証を有効にすることができません。
          </span>
        </template>
        <template #cancel><span></span></template>
      </DgrDialog>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.saml-federation-setting {
  width: 100%;
  .saml-federation-content {
    padding: 24px;
  }
  .body {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .title,
    .federation-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .activate {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 4px;

      .c-text-regular--s {
        color: $color-gray600;
      }
      .activate-tooltip {
        margin-top: 4px;
      }
    }

    .divider {
      height: 1px;
      padding: 0;
      margin: 8px 0;
      background: $color-gray400;
    }

    .sso-card {
      display: flex;
      flex-direction: column;

      box-sizing: border-box;
      background-color: #fff;
      border: 1px solid $color-gray400;
      border-radius: 4px;
      padding: 16px;

      .table-title {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .menu-button {
        border: none;
        background: none;
        cursor: pointer;
        padding: 8px;
      }
    }

    .c-btn {
      width: fit-content;
    }
    .c-btn--small {
      padding: 4px 16px;
    }
  }
}
.item-container {
  padding: 12px 0;

  .item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    height: 30px;
    padding: 4px 12px;
    border: none;
    border-radius: 0;

    background: none;
    &:hover {
      background: $color-gray400;
    }
    .icon {
      margin-right: 4px;
    }
  }
  .separator {
    margin: 8px 0;
    border-top: 1px solid $color-gray400;
  }
}
.saml-dialog {
  display: block;
  max-width: 460px;
}
.delete-button {
  color: #fff;
  background: $color-orange1000;
}
</style>
